import { atom } from 'nanostores';
import { getOrganizationMembersTokens } from '~/lib/.client/api/organizations';
import { getSortedOrgMembers } from '~/lib/.client/organizations';
import type { TokenStats } from '~/lib/.server/rateLimit';
import type { OrganizationData, OrganizationMembership } from '~/types/organization';
import type { EffectiveTier } from '~/utils/effectiveTier';
import { hmrPropertyFactory } from '~/utils/hmr';
import { withSpinner } from '~/utils/withSpinner';

type MembersMap = Map<number, OrganizationMembership[]>;
type LastFetchedMap = Map<number, number>;
export type MembersTokens = Record<number, TokenStats>;

const hmr = hmrPropertyFactory('OrganizationMembersStore');

const CACHE_DURATION = 5 * 60 * 1000;

class OrganizationMembersStore {
  @hmr()
  #membersMap = atom<MembersMap>(new Map());

  @hmr()
  #lastFetchedMap = atom<LastFetchedMap>(new Map());

  @hmr()
  activeOrgMembers = atom<OrganizationMembership[]>([]);

  @hmr()
  isMembersLoading = atom<boolean>(false);

  @hmr()
  membersTokens = atom<MembersTokens>({});

  @hmr()
  isLoadingMembersTokens = atom<boolean>(false);

  #hasValidCache(organizationId: number): boolean {
    const lastFetched = this.#lastFetchedMap.get().get(organizationId);

    if (!lastFetched) {
      return false;
    }

    const now = Date.now();

    return now - lastFetched < CACHE_DURATION;
  }

  #shouldFetchMembers(organization: OrganizationData): boolean {
    return !this.#hasValidCache(organization.id);
  }

  async fetchMembers(organization: OrganizationData, forceUpdate = false) {
    const { id } = organization;

    if (!forceUpdate && !this.#shouldFetchMembers(organization)) {
      return;
    }

    try {
      this.isMembersLoading.set(true);

      const sortedMembers = await getSortedOrgMembers(organization);

      this.activeOrgMembers.set(sortedMembers);
      this.#membersMap.set(new Map(this.#membersMap.get()).set(id, sortedMembers));
      this.#lastFetchedMap.set(new Map(this.#lastFetchedMap.get()).set(id, Date.now()));
    } catch {
      throw new Error('Failed to load your team members.');
    } finally {
      this.isMembersLoading.set(false);
    }
  }

  async fetchMembersTokens(organization: OrganizationData, ids: string[], tier: EffectiveTier) {
    this.isLoadingMembersTokens.set(true);

    try {
      await withSpinner({
        action: async () => {
          const data = await getOrganizationMembersTokens({ ids, organization, tier });

          this.membersTokens.set(data || {});
        },
        onStateUpdate: (value) => this.isLoadingMembersTokens.set(value),
      });
    } catch {
      this.membersTokens.set({});
    } finally {
      this.isLoadingMembersTokens.set(false);
    }
  }
}

export const organizationMembersStore = new OrganizationMembersStore();
